<template>
    <div class="connection-setup-wizard">
        <o-bread-crumb>
            <template v-slot:pageName>
                Connection Apps
            </template>
            <template v-slot:pageDetail>
                Setup New Connection
            </template>
            <template v-slot:pageDescription>
                <button class="btn-back" @click="cancel">
                    <i class="fas fa-arrow-left"></i> Back to List
                </button>
            </template>
        </o-bread-crumb>

        <div class="wizard-container">
            <!-- Single Card for Stepper, Content, and Actions -->
            <div class="wizard-card">
                <!-- Stepper -->
                <div class="stepper-section">
                    <div class="stepper">
                        <div v-for="(step, index) in steps" :key="index" class="step-item" :class="{
                            'active': currentStep === index + 1,
                            'completed': currentStep > index + 1
                        }">
                            <div class="step-number">
                                <span v-if="currentStep > index + 1">
                                    <i class="fas fa-check"></i>
                                </span>
                                <span v-else>{{ index + 1 }}</span>
                            </div>
                            <div class="step-label">{{ step.label }}</div>
                        </div>
                    </div>
                </div>

                <!-- Step Content -->
                <div class="content-section">
                    <!-- Step 1: Branch & Merchant Setup -->
                    <branch-merchant-setup v-if="currentStep === 1" v-model="wizardData.step1"
                        :serverErrors="validationErrors" @valid="step1Valid = $event" />

                    <!-- Step 2: Connection Setup -->
                    <connection-setup v-if="currentStep === 2" v-model="wizardData.step2"
                        :branchMerchantData="wizardData.step1" @valid="step2Valid = $event" />

                    <!-- Step 3: Credentials Display -->
                    <credentials-display v-if="currentStep === 3" :credentials="credentials" />
                </div>

                <!-- Divider -->
                <div class="card-divider"></div>

                <!-- Navigation Actions -->
                <div class="actions-section">
                    <div class="wizard-actions">
                        <button @click="cancel" class="btn-cancel" v-if="currentStep < 3">
                            Cancel
                        </button>

                        <button v-if="currentStep < 2" @click="nextStep" class="btn-primary"
                            :disabled="!step1Valid || isProcessing">
                            Next
                            <i class="fas fa-arrow-right ms-1"></i>
                        </button>

                        <button v-if="currentStep === 2" @click="submitConnection" class="btn-primary"
                            :disabled="!step2Valid || isProcessing">
                            <span v-if="isProcessing">
                                <i class="fas fa-spinner fa-spin me-1"></i>
                                Creating...
                            </span>
                            <span v-else>
                                <i class="fas fa-check me-1"></i>
                                Create Connection
                            </span>
                        </button>

                        <button v-if="currentStep === 3" @click="done" class="btn-primary">
                            <i class="fas fa-check-circle me-1"></i>
                            Done
                        </button>
                    </div>

                    <!-- Error Display -->
                    <div v-if="errorMessage" class="alert alert-danger mt-3">
                        <div class="d-flex align-items-center">
                            <i class="fas fa-exclamation-circle me-2"></i>
                            <span>{{ errorMessage }}</span>
                        </div>
                        <ul v-if="Object.keys(validationErrors).length > 0" class="mt-2 mb-0 ps-4 error-list">
                            <li v-for="(errors, field) in validationErrors" :key="field">
                                {{ formatErrorMessage(Array.isArray(errors) ? errors[0] : errors) }}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>

        <!-- Cancel Confirmation Modal -->
        <div v-if="showCancelModal" class="modal-overlay" @click.self="showCancelModal = false">
            <div class="modal-content">
                <h4>Cancel Setup?</h4>
                <p>Are you sure you want to cancel? All progress will be lost.</p>
                <div class="modal-actions">
                    <button @click="confirmCancel" class="btn-danger">
                        Yes, Cancel
                    </button>
                    <button @click="showCancelModal = false" class="btn-secondary">
                        No, Continue
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, reactive } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { OBreadCrumb } from '@/components/OmnihControl'
import BranchMerchantSetup from '@/components/ConnectionApps/BranchMerchantSetup.vue'
import ConnectionSetup from '@/components/ConnectionApps/ConnectionSetup.vue'
import CredentialsDisplay from '@/components/ConnectionApps/CredentialsDisplay.vue'

export default {
    name: 'ConnectionSetupWizard',
    components: {
        OBreadCrumb,
        BranchMerchantSetup,
        ConnectionSetup,
        CredentialsDisplay
    },
    setup() {
        const router = useRouter()
        const route = useRoute()
        const store = useStore()

        const currentStep = ref(1)
        const isProcessing = ref(false)
        const errorMessage = ref('')
        const showCancelModal = ref(false)

        const step1Valid = ref(false)
        const step2Valid = ref(false)
        const validationErrors = ref({})



        const wizardData = reactive({
            step1: {
                setup_type: route.query.mode === 'new' ? 'new' : 'existing',
                branch_id: route.query.branchId || '',
                merchant_id: ''
            },
            step2: {}
        })

        const step1Result = ref({
            branch_id: '',
            merchant_id: ''
        })

        const credentials = ref({})

        const steps = [
            { label: 'Branch & Merchant' },
            { label: 'Connection Details' },
            { label: 'Credentials' }
        ]

        /**
         * Move to the next step in the wizard.
         * Validates current step before proceeding and calls API for Step 1.
         */
        async function nextStep() {
            if (currentStep.value === 1) {
                if (!step1Valid.value) {
                    errorMessage.value = 'Please complete all required fields'
                    return
                }

                isProcessing.value = true
                errorMessage.value = ''

                try {
                    let payload = {}
                    if (wizardData.step1.setup_type === 'existing') {
                        payload = {
                            setup_type: wizardData.step1.setup_type,
                            branch_id: wizardData.step1.branch_id,
                            merchant_id: wizardData.step1.merchant_id
                        }
                    } else {
                        // Get corporate_id from business profile for root user switch corporate
                        const businessProfile = await store.dispatch('Auth/getBusinessProfile')

                        payload = {
                            setup_type: wizardData.step1.setup_type,
                            branch: {
                                ...wizardData.step1.branch,
                                ...(businessProfile.corporate_id && { corporate_id: businessProfile.corporate_id })
                            },
                            merchant: wizardData.step1.merchant
                        }
                    }

                    const step1Response = await store.dispatch('ConnectionApps/setupStep1', {
                        data: payload
                    })

                    step1Result.value = {
                        branch_id: step1Response.data.data.branch_id,
                        merchant_id: step1Response.data.data.merchant_id
                    }
                    currentStep.value = 2
                } catch (err) {
                    console.error('Error in Step 1:', err)
                    errorMessage.value = err.response?.data?.message || 'Failed to setup branch and merchant. Please try again.'
                    if (err.response?.data?.errors) {
                        validationErrors.value = err.response.data.errors
                    }
                } finally {
                    isProcessing.value = false
                }
            } else if (currentStep.value < 3) {
                currentStep.value++
                errorMessage.value = ''
            }
        }



        /**
         * Submits the connection details from Step 2 to create the connection.
         * Dispatches the setupStep2 action to the store.
         */
        async function submitConnection() {
            if (!step2Valid.value) {
                errorMessage.value = 'Please fill in all required fields'
                return
            }

            isProcessing.value = true
            errorMessage.value = ''

            try {
                const step2Data = {
                    branch_id: step1Result.value.branch_id,
                    merchant_id: step1Result.value.merchant_id,
                    property_id: wizardData.step2.property_id,
                    token_name: wizardData.step2.token_name,
                    service_account_role: wizardData.step2.service_account_role
                }

                const step2Response = await store.dispatch('ConnectionApps/setupStep2', {
                    data: step2Data
                })

                credentials.value = step2Response.data.data
                currentStep.value = 3
            } catch (err) {
                console.error('Error creating connection:', err)
                errorMessage.value = err.response?.data?.message || 'Failed to create connection. Please try again.'
            } finally {
                isProcessing.value = false
            }
        }

        function cancel() {
            showCancelModal.value = true
        }

        function confirmCancel() {
            showCancelModal.value = false
            router.push({ name: 'ConnectionApps' })
        }

        function done() {
            router.push({ name: 'ConnectionApps' })
        }

        function formatErrorMessage(msg) {
            if (!msg) return ''
            let cleanMsg = msg

            const replacements = {
                'branch.code': 'Branch Code',
                'merchant.code': 'Merchant Code',
                'branch.name': 'Branch Name',
                'merchant.name': 'Merchant Name',
            }

            Object.keys(replacements).forEach(key => {
                cleanMsg = cleanMsg.replace(key, replacements[key])
            })

            if (cleanMsg.startsWith('The ')) {
                cleanMsg = cleanMsg.substring(4)
            }

            return cleanMsg.charAt(0).toUpperCase() + cleanMsg.slice(1)
        }

        return {
            currentStep,
            isProcessing,
            errorMessage,
            showCancelModal,
            step1Valid,
            step2Valid,
            wizardData,
            step1Result,
            credentials,
            steps,
            nextStep,
            submitConnection,
            cancel,
            confirmCancel,
            done,
            validationErrors,
            formatErrorMessage
        }
    }
}
</script>

<style scoped>
/* Container - Gray Background like MarketingDetail */
.connection-setup-wizard {
    padding: 16px;
    padding-right: 20px;
    max-width: 100%;
}

/* Button Back */
.btn-back {
    background: white;
    color: #6b7280;
    border: 1px solid #d1d5db;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
}

.btn-back:hover {
    background: #f9fafb;
    border-color: #9ca3af;
}

/* Wizard Container */
.wizard-container {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

/* Single Wizard Card - White Background */
.wizard-card {
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    overflow: hidden;
}

/* Stepper Section */
.stepper-section {
    padding: 1.5rem;
}

.stepper {
    display: flex;
    justify-content: space-between;
    position: relative;
}

.stepper::before {
    content: '';
    position: absolute;
    top: 20px;
    left: calc(100% / 6);
    right: calc(100% / 6);
    height: 3px;
    background: #e5e7eb;
    z-index: 0;
}

.step-item {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    z-index: 1;
}

.step-number {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: white;
    border: 2px solid #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    color: #9ca3af;
    margin-bottom: 0.5rem;
    transition: all 0.3s ease;
}

.step-item.active .step-number {
    background: var(--primary-color, #F93E3E);
    border-color: var(--primary-color, #F93E3E);
    color: white;
}

.step-item.completed .step-number {
    background: var(--primary-color, #10b981);
    border-color: var(--primary-color, #10b981);
    color: white;
}

.step-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
    text-align: center;
}

.step-item.active .step-label {
    color: var(--primary-color, #F93E3E);
    font-weight: 600;
}

.step-item.completed .step-label {
    color: var(--primary-color, #10b981);
}

/* Card Divider - Gradient fade at edges */
.card-divider {
    height: 1px;
    background: linear-gradient(to right,
            transparent 0%,
            #e5e7eb 5%,
            #e5e7eb 95%,
            transparent 100%);
}

/* Actions Section */
.actions-section {
    padding: 1.5rem;
}

.wizard-actions {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
}

.btn-primary,
.btn-secondary,
.btn-cancel {
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 500;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
    display: flex;
    align-items: center;
}

.btn-primary {
    background: var(--primary-color, #F93E3E);
    color: white;
    margin-left: auto;
}

.btn-primary:hover:not(:disabled) {
    filter: brightness(0.9);
    transform: translateY(-1px);
}

.btn-primary:disabled {
    background: #9ca3af;
    cursor: not-allowed;
}

.btn-secondary {
    background: white;
    color: #6b7280;
    border: 1px solid #d1d5db;
}

.btn-secondary:hover {
    background: #f9fafb;
    border-color: #9ca3af;
}

.btn-cancel {
    background: #ffffff;
    color: var(--primary-color, #F93E3E);
    border: 1px solid var(--primary-border, #F93E3E);
}

.btn-cancel:hover {
    background: var(--secondary-color, #F93E3E);
}

.alert {
    padding: 1rem;
    border-radius: 8px;
    font-size: 0.875rem;
}

.alert-danger {
    background: #fee2e2;
    color: #991b1b;
    border: 1px solid #fecaca;
}

/* Modal Styles */
.modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 1rem;
}

.modal-content {
    background: white;
    border-radius: 16px;
    padding: 2rem;
    max-width: 500px;
    width: 90%;
}

.modal-content h4 {
    margin-bottom: 1rem;
    color: #111827;
    font-size: 1.25rem;
    font-weight: 600;
}

.modal-content p {
    margin-bottom: 1.5rem;
    color: #6b7280;
    font-size: 0.9375rem;
}

.modal-actions {
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
    margin-top: 1.5rem;
}

.btn-danger {
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 500;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
    background: #dc2626;
    color: white;
}

.btn-danger:hover {
    background: #b91c1c;
}

@media (max-width: 768px) {
    .stepper {
        flex-direction: column;
        gap: 1rem;
    }

    .stepper::before {
        display: none;
    }

    .step-item {
        flex-direction: row;
        justify-content: flex-start;
    }

    .step-number {
        margin-right: 1rem;
        margin-bottom: 0;
    }

    .wizard-actions {
        flex-direction: column;
    }

    .btn-primary {
        margin-left: 0;
    }
}

.error-list {
    list-style-type: disc;
    font-size: 0.85rem;
    color: #991b1b;
}

.error-list li {
    margin-bottom: 0.25rem;
}
</style>
